/**
 * Shared reference-link row used by post cards in view and read-only edit modes.
 */
import React from "react";
import { getLinkIcon } from "util/transformHtmlContent";

export type ReferencePlatform =
  | "youtube"
  | "tiktok"
  | "instagram"
  | "x"
  | "facebook"
  | "reddit"
  | "wikipedia"
  | "custom";

/**
 * Maps a URL to the icon family already supported by `getLinkIcon`.
 */
export function getReferencePlatform(url: string): ReferencePlatform {
  const lowered = url.toLowerCase();

  if (lowered.includes("youtube.com") || lowered.includes("youtu.be")) {
    return "youtube";
  }
  if (lowered.includes("tiktok.com")) {
    return "tiktok";
  }
  if (lowered.includes("instagram.com")) {
    return "instagram";
  }
  if (lowered.includes("x.com") || lowered.includes("twitter.com")) {
    return "x";
  }
  if (lowered.includes("facebook.com")) {
    return "facebook";
  }
  if (lowered.includes("reddit.com")) {
    return "reddit";
  }
  if (lowered.includes("wikipedia.org")) {
    return "wikipedia";
  }

  return "custom";
}

interface PostReferenceLinkProps {
  url: string;
  textClassName?: string;
  containerClassName?: string;
  iconWrapperClassName?: string;
}

const PostReferenceLink: React.FC<PostReferenceLinkProps> = ({
  url,
  textClassName = "text-xs",
  containerClassName = "w-full min-w-0",
  iconWrapperClassName = "external-link inline-flex items-center flex-shrink-0",
}) => {
  if (!url.trim()) {
    return null;
  }

  const platform = getReferencePlatform(url);

  return (
    <div className={containerClassName}>
      <div className="inline-flex items-center gap-1">
        <a
          className={`${iconWrapperClassName} ${textClassName}`.trim()}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open reference link"
        >
          {getLinkIcon(platform, url)}
        </a>
        <a
          className={`external-link line-clamp-1 break-all ${textClassName}`.trim()}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      </div>
    </div>
  );
};

export default PostReferenceLink;
